import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  isPhoenixCursorEnabled,
  setPhoenixCursorEnabled,
} from "@/components/PhoenixCursor";
import {
  usePushState,
  subscribeToPush,
  unsubscribeFromPush,
  sendTestPush,
} from "@/components/pushClient";

/**
 * Display Preferences card on the Profile page.
 * Currently houses the Phoenix Flame cursor toggle. Future preference
 * toggles (motion-reduce, theme variants, etc.) drop in here.
 */
export default function DisplayPreferences() {
  const [cursorOn, setCursorOn] = useState(() => isPhoenixCursorEnabled());
  // Detect touch / coarse-pointer devices — cursor has no effect there
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const { perm, subscribed, supported, refresh } = usePushState();
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => {
    try {
      const fine = window.matchMedia("(pointer: fine)").matches;
      const noHover = window.matchMedia("(hover: none)").matches;
      setIsTouchDevice(!fine || noHover);
    } catch {
      setIsTouchDevice(false);
    }
  }, []);

  function toggleCursor() {
    const next = !cursorOn;
    setCursorOn(next);
    setPhoenixCursorEnabled(next);
    toast.success(
      next
        ? "🐦‍🔥 Phoenix Flame cursor activated"
        : "Default cursor restored",
      { duration: 2800 }
    );
  }

  async function togglePush() {
    if (pushBusy) return;
    setPushBusy(true);
    try {
      if (subscribed) {
        await unsubscribeFromPush();
        toast.success("🔕 Browser notifications turned off");
      } else {
        await subscribeToPush();
        toast.success("🔔 Browser notifications enabled");
      }
      await refresh();
    } catch (e) {
      toast.error(e?.message || "Couldn't change push settings.");
    } finally {
      setPushBusy(false);
    }
  }

  async function handleTestPush() {
    try {
      await sendTestPush();
      toast.success("Sent — check for the system notification!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test push failed.");
    }
  }

  return (
    <div
      data-testid="display-preferences"
      className="bg-[#0F0F0F] border border-zinc-800 p-6 mt-6"
    >
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-1">
        Display
      </div>
      <h2 className="font-display text-2xl text-white mb-4">
        Preferences
      </h2>

      <div className="flex items-center justify-between gap-4 py-3 border-t border-zinc-800">
        <div className="flex-1 min-w-0">
          <div className="font-display text-base text-white flex items-center gap-2">
            <span>🐦‍🔥</span> Phoenix Flame Cursor
          </div>
          <p className="text-xs text-neutral-500 mt-1 leading-relaxed">
            Replace your mouse pointer with a glowing flame trail. Pulses on
            click, expands on hover. Saved locally — only affects this device.
            {isTouchDevice && (
              <span className="block mt-1 text-[#D4AF37] italic">
                Not available on touch devices.
              </span>
            )}
          </p>
        </div>
        <button
          type="button"
          data-testid="cursor-toggle"
          role="switch"
          aria-checked={cursorOn}
          aria-label="Toggle Phoenix Flame cursor"
          disabled={isTouchDevice}
          onClick={toggleCursor}
          className={`relative shrink-0 w-14 h-7 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
            cursorOn
              ? "bg-[#D4AF37]"
              : "bg-zinc-800 hover:bg-zinc-700"
          }`}
        >
          <span
            className={`absolute top-0.5 w-6 h-6 bg-black transition-transform ${
              cursorOn ? "translate-x-7" : "translate-x-0.5"
            }`}
          />
          <span className="sr-only">{cursorOn ? "On" : "Off"}</span>
        </button>
      </div>

      {/* Browser push notifications */}
      <div className="flex items-center justify-between gap-4 py-3 border-t border-zinc-800">
        <div className="flex-1 min-w-0">
          <div className="font-display text-base text-white flex items-center gap-2">
            <span>🔔</span> Browser Notifications
          </div>
          <p className="text-xs text-neutral-500 mt-1 leading-relaxed">
            Get streak reminders, DMs, and friend pings even when this tab is
            closed. You can disable any time.
            {!supported && (
              <span className="block mt-1 text-[#D4AF37] italic">
                Not supported in this browser. iOS Safari needs the app added
                to your home screen first.
              </span>
            )}
            {supported && perm === "denied" && (
              <span className="block mt-1 text-red-400 italic">
                Permission blocked. Re-enable notifications in your browser's
                site settings, then refresh.
              </span>
            )}
          </p>
          {subscribed && (
            <button
              type="button"
              data-testid="push-send-test"
              onClick={handleTestPush}
              className="mt-2 text-[10px] uppercase tracking-widest text-[#D4AF37] hover:text-white font-bold"
            >
              Send a test ping →
            </button>
          )}
        </div>
        <button
          type="button"
          data-testid="push-toggle"
          role="switch"
          aria-checked={subscribed}
          aria-label="Toggle browser notifications"
          disabled={!supported || perm === "denied" || pushBusy}
          onClick={togglePush}
          className={`relative shrink-0 w-14 h-7 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
            subscribed ? "bg-[#D4AF37]" : "bg-zinc-800 hover:bg-zinc-700"
          }`}
        >
          <span
            className={`absolute top-0.5 w-6 h-6 bg-black transition-transform ${
              subscribed ? "translate-x-7" : "translate-x-0.5"
            }`}
          />
          <span className="sr-only">{subscribed ? "On" : "Off"}</span>
        </button>
      </div>
    </div>
  );
}
